import { Component, Input, ChangeDetectionStrategy, forwardRef, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'hc-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HcInputComponent),
      multi: true
    }
  ]
})
export class HcInputComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'url' | 'tel' | 'date' | 'time' | 'datetime-local' | 'search' = 'text';
  @Input() leftIcon = '';
  @Input() rightIcon = '';
  @Input() inputId = '';
  @Input() hasError: boolean | null | undefined = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() autocomplete = '';

  // Prefix/suffix text inside the input container (e.g., "CHF", "%", "ID")
  @Input() prefix = '';
  @Input() suffix = '';

  // Numeric-only restriction
  @Input() numbersOnly = false;

  // Native input attributes (pass-through)
  @Input() min: string | number = '';
  @Input() max: string | number = '';
  @Input() step: string | number = '';
  @Input() maxlength: string | number = '';
  @Input() minlength: string | number = '';
  @Input() pattern = '';
  @Input() name = '';

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  value = '';
  focused = false;
  passwordVisible = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: string): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
  }

  onKeypress(event: KeyboardEvent): void {
    if (this.numbersOnly) {
      const allowed = /[0-9.,-]/;
      if (!allowed.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab' && event.key !== 'Enter') {
        event.preventDefault();
      }
    }
  }

  onFocus(): void {
    this.focused = true;
  }

  onBlur(): void {
    this.focused = false;

    // Some browsers/password managers fill the native input without firing an
    // `input` event, leaving `value` (and the bound FormControl) stale even
    // though the field visually shows the autofilled text. Re-sync from the
    // live DOM value here, since blur reliably fires before a submit click.
    const domValue = this.inputEl?.nativeElement.value ?? '';
    if (domValue !== this.value) {
      this.value = domValue;
      this.onChange(this.value);
    }

    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  /** The actual type rendered on the native input — handles password toggle */
  get effectiveType(): string {
    if (this.type === 'password' && this.passwordVisible) return 'text';
    return this.type;
  }

  /** Whether to show the built-in password toggle icon */
  get showPasswordToggle(): boolean {
    return this.type === 'password';
  }

  get containerClass(): string {
    const classes = ['hc-input'];
    if (this.focused) classes.push('hc-input--focused');
    if (this.hasError) classes.push('hc-input--error');
    if (this.disabled) classes.push('hc-input--disabled');
    if (this.readonly) classes.push('hc-input--readonly');
    if (this.value) classes.push('hc-input--filled');
    return classes.join(' ');
  }
}
